import { useMemo } from "react";
import {
  AssetClassLoadType,
  useAssetClasses,
} from "@app/features/assets/useAssetClasses";
import {
  SimulationJobResultAssetClass,
  useSimulationQaReportSummaryCreateQuery,
} from "@app/api/carApi.generated";
import { DecimalOptions, formatDateTime, isDefined } from "@app/utils";
import { useAssetClassGroupSelector } from "@app/hooks/useAssetClassGroupSelector";
import {
  ExcelDataFormat,
  ExcelTable,
  useExcelExport,
} from "@app/hooks/useExcelExport";
import { pendoClasses } from "@app/core/thirdParty/pendo";

interface UseSimulationDetailsSummaryParams {
  simulationJobId: string;
}

interface SimulationSummaryItem extends SimulationJobResultAssetClass {
  id?: string;
  acLevel4Id: string;
  acName: string;
  isEquity: boolean;
  isFixedIncome: boolean;
  isCash: boolean;
  isCommodity: boolean;
  isRealEstate: boolean;
}

const decimalOptions: DecimalOptions = {
  decimalPlaces: 2,
  forceShowDecimals: true,
};

export const useSimulationDetailsSummary = (
  params: UseSimulationDetailsSummaryParams,
) => {
  const assetClasses = useAssetClasses({
    type: AssetClassLoadType.bySimulationJobId,
    simulationJobId: params.simulationJobId,
  });

  const assetClassGroupSelector = useAssetClassGroupSelector();

  const { data, isLoading } = useSimulationQaReportSummaryCreateQuery({
    simulationQaReportRequestBase: {
      simulation_job_id: params.simulationJobId,
    },
  });

  const excelExport = useExcelExport();

  return useMemo(() => {
    const items =
      data?.asset_classes
        .map<SimulationSummaryItem | undefined>((i) => {
          const ac = assetClasses.items.find(
            (ac) => ac.level4Id === i.stat_result.asset_class4,
          );
          if (!ac) {
            return undefined;
          }

          return {
            acLevel4Id: ac.level4Id,
            acName: ac.name,
            isEquity: ac.isEquity,
            isFixedIncome: ac.isFixedIncome,
            isCash: ac.isCash,
            isCommodity: ac.isCommodity,
            isRealEstate: ac.isRealEstate,
            ...i.stat_result,
          };
        })
        .filter(isDefined)
        .filter((i) =>
          assetClassGroupSelector.isAssetClassInGroup(i.acLevel4Id),
        ) ?? [];

    const equityItems = items.filter((i) => i.isEquity);
    const fixedIncomeItems = items.filter((i) => i.isFixedIncome || i.isCash);
    const fixedIncomeSpreadItems = items.filter((i) => i.isFixedIncome);
    const commodityItems = items.filter((i) => i.isCommodity);
    const realEstateItems = items.filter((i) => i.isRealEstate);

    const tables: ExcelTable[] = [];

    if (data?.static_simulation) {
      tables.push({
        name: "Asset Classes",
        columns: [
          {
            label: "",
            format: ExcelDataFormat.general,
            fraction: 1.5,
            charWidth: 40,
          },
          {
            label: "Average Historical Annual Return",
            pendoClassName:
              pendoClasses.simulationSummaryACExpectedTenYearReturn,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Simulated Real Return",
            pendoClassName:
              pendoClasses.simulationSummaryACSimulatedTenYearRealReturn,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Simulated Nominal Return",
            pendoClassName:
              pendoClasses.simulationSummaryACSimulatedTenYearNominalReturn,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Input Volatility",
            pendoClassName: pendoClasses.simulationSummaryACInputVolatility,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Output Volatility (Real)",
            pendoClassName:
              pendoClasses.simulationSummaryACOutputVolatilityReal,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Simulated 1 Year Loss (Real)",
            pendoClassName:
              pendoClasses.simulationSummaryACSimulatedOneYearLossReal,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
        ],
        rows: items.map((i) => [
          i.acName,
          i.ten_year_expected_return,
          i.output_real_return_mean,
          i.output_nominal_return_mean,
          i.input_return_sigma,
          i.output_real_return_sigma,
          i.one_year_downside_real_return,
        ]),
      });
    } else {
      const getEquitiesLikeTable = (
        name: string,
        items: SimulationSummaryItem[],
      ): ExcelTable => ({
        name,
        columns: [
          {
            label: "",
            format: ExcelDataFormat.general,
            fraction: 1.5,
            charWidth: 40,
          },
          {
            label: "Expected Return",
            pendoClassName:
              pendoClasses.simulationSummaryEquitiesExpectedTenYearReturn,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Simulated 10 Year Real Return",
            pendoClassName:
              pendoClasses.simulationSummaryEquitiesSimulatedTenYearRealReturn,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Simulated 10 Year Nominal Return",
            pendoClassName:
              pendoClasses.simulationSummaryEquitiesSimulatedTenYearNominalReturn,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Input Volatility",
            pendoClassName:
              pendoClasses.simulationSummaryEquitiesInputVolatility,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Output Volatility (Real)",
            pendoClassName:
              pendoClasses.simulationSummaryEquitiesOutputVolatilityReal,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
          {
            label: "Simulated 1 Year Loss (Real)",
            pendoClassName:
              pendoClasses.simulationSummaryEquitiesSimulatedOneYearLossReal,
            format: ExcelDataFormat.percent,
            decimalOptions,
          },
        ],
        rows: items.map((i) => [
          i.acName,
          i.ten_year_expected_return,
          i.output_real_return_mean,
          i.output_nominal_return_mean,
          i.input_return_sigma,
          i.output_real_return_sigma,
          i.one_year_downside_real_return,
        ]),
      });

      if (equityItems.length > 0) {
        tables.push(getEquitiesLikeTable("Equities", equityItems));
      }

      if (fixedIncomeItems.length > 0) {
        tables.push({
          name: "Fixed Income",
          columns: [
            {
              label: "",
              format: ExcelDataFormat.general,
              fraction: 1.5,
              charWidth: 40,
            },
            {
              label: "Simulated Real Return",
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Simulated Nominal Return",
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Simulated Return Volatility (Nominal)",
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Simulated Yield Volatility",
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Simulated 1 Year Loss (Real)",
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
          ],
          rows: fixedIncomeItems.map((i) => [
            i.acName,
            i.output_real_return_mean,
            i.output_nominal_return_mean,
            i.output_nominal_return_sigma,
            i.output_yield_sigma,
            i.one_year_downside_real_return,
          ]),
        });
      }

      if (fixedIncomeSpreadItems.length > 0) {
        tables.push({
          name: "Fixed Income Spreads",
          columns: [
            {
              label: "",
              format: ExcelDataFormat.general,
              fraction: 1.5,
              charWidth: 40,
            },
            {
              label: "Historical Average Spread",
              pendoClassName:
                pendoClasses.simulationSummaryFixedIncomeSpreadsHistoricalAverageSpread,
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Simulated Average Spread",
              pendoClassName:
                pendoClasses.simulationSummaryFixedIncomeSpreadsSimulatedAverageSpread,
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Historic Spread Volatility",
              pendoClassName:
                pendoClasses.simulationSummaryFixedIncomeSpreadsHistoricSpreadVolatility,
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
            {
              label: "Simulated Spread Volatility",
              pendoClassName:
                pendoClasses.simulationSummaryFixedIncomeSpreadsSimulatedSpreadVolatility,
              format: ExcelDataFormat.percent,
              decimalOptions,
            },
          ],
          rows: fixedIncomeSpreadItems.map((i) => [
            i.acName,
            i.input_spread_mean,
            i.output_spread_mean,
            i.input_spread_sigma,
            i.output_spread_sigma,
          ]),
        });
      }

      if (commodityItems.length > 0) {
        tables.push(getEquitiesLikeTable("Commodities", commodityItems));
      }
      if (realEstateItems.length > 0) {
        tables.push(getEquitiesLikeTable("Real Estate", realEstateItems));
      }
    }

    const handleDownloadReports = () => {
      excelExport.exportAllExcel({
        fileName: `Simulation Summary - ${data?.simulation_name} - ${formatDateTime(
          data?.simulation_date,
        )
          .replaceAll("/", "-")
          .replaceAll(":", "-")} - ${
          assetClassGroupSelector.items.find(
            (i) => i.code === assetClassGroupSelector.value,
          )?.name
        }`,
        tables,
      });
    };

    return {
      isLoading:
        assetClasses.isLoading ||
        assetClassGroupSelector.isLoading ||
        isLoading,
      assetClassGroupSelector,
      tables,
      handleDownloadReports,
    };
  }, [assetClassGroupSelector, assetClasses, excelExport, data, isLoading]);
};
